import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Save, Mail, Trash2 } from "lucide-react";

const TRIGGER_LABELS = {
  recruiter_approved: "Recruiter approved",
  recruiter_rejected: "Recruiter rejected",
  welcome_candidate: "Welcome — candidate",
  welcome_recruiter: "Welcome — recruiter / moderator",
  email_verification: "Email verification (change)",
  password_reset: "Password reset",
};

const VARS_BY_TRIGGER = {
  recruiter_approved: ["user_name", "user_email", "company_name", "recruiter_name", "site_name", "cta_url"],
  recruiter_rejected: ["user_name", "user_email", "company_name", "denial_reason", "denial_note", "recruiter_name", "site_name", "cta_url"],
  welcome_candidate: ["user_name", "user_email", "company_name", "site_name", "verify_url", "cta_url"],
  welcome_recruiter: ["user_name", "user_email", "company_name", "site_name", "verify_url", "cta_url"],
  email_verification: ["user_name", "user_email", "site_name", "verify_url", "cta_url"],
  password_reset: ["user_name", "user_email", "site_name", "reset_url", "cta_url"],
};

const inputCls = "w-full border border-[#0F0F0F] bg-transparent px-3 py-2";

export default function PlatformEmailTemplatesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("recruiter_approved");
  const [form, setForm] = useState({ subject: "", body_html: "", is_enabled: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/platform-email-templates");
      setData(data);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!data) return;
    const existing = (data.templates || []).find((t) => t.trigger === active);
    setForm(existing ? { subject: existing.subject || "", body_html: existing.body_html || "", is_enabled: !!existing.is_enabled }
                     : { subject: "", body_html: "", is_enabled: true });
    setError("");
  }, [active, data]);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true); setError(""); setSuccess("");
    try {
      await api.put(`/admin/platform-email-templates/${encodeURIComponent(active)}`, form);
      setSuccess("Template saved.");
      const { data } = await api.get("/admin/platform-email-templates");
      setData(data);
      // Keep success visible for 3s
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this template? The default system email will be used again.")) return;
    try {
      await api.delete(`/admin/platform-email-templates/${encodeURIComponent(active)}`);
      const { data } = await api.get("/admin/platform-email-templates");
      setData(data);
      setSuccess("Template removed — using default copy.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  if (loading) return <div className="mt-6 text-[#5C5C5C] text-sm">Loading…</div>;
  if (!data) return null;

  const triggers = data.available_triggers || [];
  const configured = new Set((data.templates || []).map((t) => t.trigger));
  const vars = VARS_BY_TRIGGER[active] || ["user_name", "user_email", "site_name", "cta_url"];

  return (
    <div className="mt-6" data-testid="platform-templates-section">
      <div className="border border-[var(--brand-primary)]/40 bg-[var(--brand-primary)]/5 p-4 mb-6">
        <div className="label-eyebrow text-[var(--brand-primary)] flex items-center gap-1.5"><Mail className="w-3 h-3" /> Platform email templates · Owner</div>
        <p className="text-sm text-[#5C5C5C] mt-2">
          Customise the platform-wide system emails Ocean Ash sends on your behalf. When you leave a template empty,
          the default copy is used. Use the variables shown below the editor.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6" data-testid="platform-templates-editor">
        <aside className="border border-[#E2E0D8] p-3 space-y-1">
          <div className="label-eyebrow flex items-center gap-1.5"><Mail className="w-3 h-3" /> System triggers</div>
          {triggers.map((t) => (
            <button
              key={t}
              data-testid={`ptpl-trigger-${t}`}
              onClick={() => setActive(t)}
              className={`w-full text-left text-sm px-3 py-2 flex items-center justify-between ${active === t ? "bg-[#0F0F0F] text-[#FDFCF8]" : "hover:bg-[#E2E0D8]/40"}`}
            >
              <span className="truncate">{TRIGGER_LABELS[t] || t}</span>
              {configured.has(t) && <span className="text-[10px] uppercase font-semibold tracking-widest">ON</span>}
            </button>
          ))}
        </aside>

        <form onSubmit={save} className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="label-eyebrow">Editing</div>
              <h2 className="font-display font-black text-2xl mt-0.5">{TRIGGER_LABELS[active] || active}</h2>
            </div>
            <label className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold">
              <input
                data-testid="ptpl-enabled"
                type="checkbox"
                checked={form.is_enabled}
                onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
              />
              Enabled
            </label>
          </div>

          <div>
            <label className="label-eyebrow block mb-1.5">Subject</label>
            <input data-testid="ptpl-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Welcome {{user_name}} — your account is ready" className={inputCls} />
          </div>

          <div>
            <label className="label-eyebrow block mb-1.5">Body (HTML allowed)</label>
            <textarea data-testid="ptpl-body" rows={12} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} placeholder="<p>Hi {{user_name}},</p><p>Your account at {{site_name}} is now active.</p>" className={`${inputCls} font-mono text-xs`} />
          </div>

          <div className="text-xs text-[#5C5C5C]">
            <span className="font-semibold">Variables for this trigger: </span>
            {vars.map((t) => (
              <code key={t} className="ml-2 px-1.5 py-0.5 bg-[#E2E0D8] text-[#0F0F0F]">{`{{${t}}}`}</code>
            ))}
          </div>

          {error && <div data-testid="ptpl-error" className="text-sm text-[var(--brand-primary)]">{error}</div>}
          {success && <div data-testid="ptpl-success" className="text-sm text-green-700">{success}</div>}

          <div className="flex items-center gap-2">
            <button type="submit" disabled={saving || !form.subject.trim() || !form.body_html.trim()} data-testid="ptpl-save" className="btn-terra disabled:opacity-40">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save template"}
            </button>
            {configured.has(active) && (
              <button type="button" onClick={remove} data-testid="ptpl-delete" className="btn-ghost text-sm text-[var(--brand-primary)]">
                <Trash2 className="w-4 h-4" /> Revert to default
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
